import { useMemo, useState } from 'react'
import { ChevronUp, ChevronDown, Plus, Check, X, RotateCcw } from 'lucide-react'
import {
  useAdminEventTags,
  useAdminCreateEventTag,
  useAdminUpdateEventTag,
  useAdminDeleteEventTag,
} from '../../hooks/useAdmin'
import type { EventTag, EventTagCategory } from '@fellowship/shared'

const CATEGORIES: { key: EventTagCategory; label: string }[] = [
  { key: 'dietary', label: 'Dietary' },
  { key: 'audience', label: 'Audience' },
]

function TagRow({
  tag,
  isFirst,
  isLast,
  onMove,
}: {
  tag: EventTag
  isFirst: boolean
  isLast: boolean
  onMove: (direction: -1 | 1) => void
}) {
  const update = useAdminUpdateEventTag()
  const remove = useAdminDeleteEventTag()
  const [editing, setEditing] = useState(false)
  const [label, setLabel] = useState(tag.label)

  function saveLabel() {
    const next = label.trim()
    if (next && next !== tag.label) update.mutate({ id: tag.id, label: next })
    setEditing(false)
  }

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-[10px] border-[1.5px] ${tag.isActive ? 'bg-s2 border-line-soft' : 'bg-transparent border-dashed border-line-soft opacity-60'}`}
    >
      {/* Reorder */}
      <div className="flex flex-col">
        <button
          aria-label="Move up"
          disabled={isFirst}
          onClick={() => onMove(-1)}
          className="text-muted hover:text-ink disabled:opacity-25 disabled:cursor-not-allowed"
        >
          <ChevronUp size={14} />
        </button>
        <button
          aria-label="Move down"
          disabled={isLast}
          onClick={() => onMove(1)}
          className="text-muted hover:text-ink disabled:opacity-25 disabled:cursor-not-allowed"
        >
          <ChevronDown size={14} />
        </button>
      </div>

      {/* Label */}
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveLabel()
                if (e.key === 'Escape') {
                  setLabel(tag.label)
                  setEditing(false)
                }
              }}
              className="w-full max-w-[280px] px-2 py-1 text-[13px] rounded-md border border-line-soft bg-bg"
              aria-label="Tag label"
            />
            <button aria-label="Save label" onClick={saveLabel} className="text-primary">
              <Check size={15} />
            </button>
            <button
              aria-label="Cancel edit"
              onClick={() => {
                setLabel(tag.label)
                setEditing(false)
              }}
              className="text-muted"
            >
              <X size={15} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="text-[14px] font-medium text-left hover:underline"
            title="Click to rename"
          >
            {tag.label}
          </button>
        )}
        <div className="text-[11px] text-muted font-mono mt-0.5">{tag.slug}</div>
      </div>

      {/* Category */}
      <select
        value={tag.category}
        onChange={(e) => update.mutate({ id: tag.id, category: e.target.value as EventTagCategory })}
        aria-label="Category"
        className="text-[12px] px-2 py-1 rounded-md border border-line-soft bg-bg"
      >
        {CATEGORIES.map((c) => (
          <option key={c.key} value={c.key}>
            {c.label}
          </option>
        ))}
      </select>

      {/* Activate / deactivate */}
      {tag.isActive ? (
        <button
          onClick={() => {
            if (confirm(`Hide "${tag.label}"? It will disappear from event forms and the Discover filter. Past events keep it.`))
              remove.mutate(tag.id)
          }}
          className="text-[12px] text-danger px-2 py-1 rounded-md hover:bg-danger-soft"
        >
          Hide
        </button>
      ) : (
        <button
          onClick={() => update.mutate({ id: tag.id, isActive: true })}
          className="flex items-center gap-1 text-[12px] text-primary px-2 py-1 rounded-md hover:bg-primary-soft"
        >
          <RotateCcw size={12} /> Restore
        </button>
      )}
    </div>
  )
}

function AddTagForm() {
  const create = useAdminCreateEventTag()
  const [label, setLabel] = useState('')
  const [category, setCategory] = useState<EventTagCategory>('dietary')

  function submit() {
    const next = label.trim()
    if (!next) return
    create.mutate(
      { label: next, category },
      { onSuccess: () => setLabel('') },
    )
  }

  return (
    <div className="flex items-center gap-2 mb-6">
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
        placeholder="New tag label (e.g. Wheelchair accessible)"
        aria-label="New tag label"
        className="flex-1 max-w-[360px] px-3 py-2 text-[13px] rounded-[10px] border border-line-soft bg-bg"
      />
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value as EventTagCategory)}
        aria-label="New tag category"
        className="text-[13px] px-2 py-2 rounded-[10px] border border-line-soft bg-bg"
      >
        {CATEGORIES.map((c) => (
          <option key={c.key} value={c.key}>
            {c.label}
          </option>
        ))}
      </select>
      <button
        onClick={submit}
        disabled={!label.trim() || create.isPending}
        className="flex items-center gap-1.5 text-[13px] font-semibold px-3 py-2 rounded-[10px] bg-primary text-white disabled:opacity-50"
      >
        <Plus size={15} /> Add
      </button>
      {create.isError && (
        <span className="text-[12px] text-danger">
          {create.error instanceof Error ? create.error.message : 'Failed'}
        </span>
      )}
    </div>
  )
}

export default function AdminEventTagsPage() {
  const { data, isLoading } = useAdminEventTags()
  const update = useAdminUpdateEventTag()
  const tags = useMemo(() => data ?? [], [data])

  // Reorder by swapping sort_order with the adjacent sibling in the same category.
  function move(rows: EventTag[], index: number, direction: -1 | 1) {
    const neighbor = rows[index + direction]
    const current = rows[index]
    if (!neighbor || !current) return
    update.mutate({ id: current.id, sortOrder: neighbor.sortOrder })
    update.mutate({ id: neighbor.id, sortOrder: current.sortOrder })
  }

  const grouped = useMemo(() => {
    const by: Record<EventTagCategory, EventTag[]> = { dietary: [], audience: [] }
    for (const t of tags) by[t.category]?.push(t)
    for (const k of Object.keys(by) as EventTagCategory[]) {
      by[k].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label))
    }
    return by
  }, [tags])

  return (
    <div>
      <div className="mb-6">
        <h1 className="m-0 text-[24px] font-bold">Event tags</h1>
        <p className="text-[13px] text-muted mt-2 m-0 max-w-[620px] leading-[1.5]">
          The dietary &amp; audience attribute filters shown on event forms and the Discover
          filter. Add, rename, recategorize, reorder, or hide tags here. Renaming is safe — it
          never breaks existing events. Hiding removes a tag from forms but keeps it on past
          events that already used it.
        </p>
      </div>

      <AddTagForm />

      {isLoading ? (
        <div className="text-[13px] text-muted">Loading…</div>
      ) : (
        <div className="flex flex-col gap-8">
          {CATEGORIES.map((cat) => {
            const rows = grouped[cat.key]
            return (
              <section key={cat.key}>
                <h2 className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted mb-3">
                  {cat.label}
                </h2>
                {rows.length === 0 ? (
                  <div className="text-[13px] text-muted">No tags in this category.</div>
                ) : (
                  <div className="flex flex-col gap-2 max-w-[640px]">
                    {rows.map((tag, i) => (
                      <TagRow
                        key={tag.id}
                        tag={tag}
                        isFirst={i === 0}
                        isLast={i === rows.length - 1}
                        onMove={(dir) => move(rows, i, dir)}
                      />
                    ))}
                  </div>
                )}
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}
